import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { canUserCreateCases } from '@/utils/constants/tiers'
import logger from '@/utils/logger'

/**
 * Vue Router Configuration for Olvidadme
 *
 * Implementing Router Guards pattern from PATTERNS_FROM_BASKET_BASE.md
 * Following DASHBOARD_PERSISTENT_NAVIGATION_PLAN.md Phase 2 optimizations
 *
 * Features:
 * - Authentication guards for protected routes
 * - Automatic redirects for authenticated/unauthenticated users
 * - Route-based access control with tier enforcement
 * - Auth state initialization on app startup
 * - Enhanced breadcrumb integration for persistent navigation
 * - Consistent meta properties across all route categories
 *
 * Breadcrumb Integration (Phase 2):
 * - All protected routes include breadcrumb meta property
 * - Dashboard hierarchy: "Dashboard > Section" pattern
 * - Admin hierarchy: "Admin > Section" pattern
 * - GDPR hierarchy: "Dashboard > GDPR > Section" pattern
 * - Dynamic routes use null path for breadcrumb endpoints
 * - Compatible with useBreadcrumb.js composable and AdminLayoutBase
 *
 * Route Organization:
 * - Public routes (no auth required)
 * - Authentication routes (guest only)
 * - Payment routes (post-Stripe redirect handling)
 * - Legal/GDPR compliance routes (public access)
 * - Service catalog routes (public marketing)
 * - Protected routes (authenticated users with tier control)
 * - GDPR routes (authenticated users, privacy controls)
 * - Admin routes (admin role required)
 * - Blog admin routes (admin content management)
 * - Public blog routes (SEO optimized)
 * - Error routes (with proper SEO directives)
 */

const routes = [
  // ==========================================
  // PUBLIC ROUTES (No authentication required)
  // ==========================================
  
  {
    path: '/',
    name: 'LandingPage',
    component: () => import('@/views/LandingPage.vue'),
    meta: {
      title: 'Olvidadme - Protege tu Privacidad Digital | Derecho al Olvido y Gestión GDPR'
    }
  },
  {
    path: '/auth/login',
    name: 'Login',
    component: () => import('@/views/auth/Login.vue'),
    meta: {
      requiresGuest: true, // Only for non-authenticated users
      title: 'Iniciar Sesión - Olvidadme'
    }
  },
  {
    path: '/auth/register',
    name: 'Register', 
    component: () => import('@/views/auth/Register.vue'),
    meta: {
      requiresGuest: true, // Only for non-authenticated users
      title: 'Crear Cuenta - Olvidadme'
    }
  },
  {
    path: '/auth/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPassword.vue'),
    meta: {
      requiresGuest: true,
      title: 'Recuperar Contraseña - Olvidadme'
    }
  },
  {
    path: '/auth/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/auth/ResetPassword.vue'),
    meta: {
      requiresGuest: true,
      title: 'Restablecer Contraseña - Olvidadme'
    }
  },
  {
    path: '/auth/verify-email',
    name: 'VerifyEmail',
    component: () => import('@/views/auth/VerifyEmail.vue'),
    meta: {
      title: 'Verificar Email - Olvidadme'
    }
  },
  {
    path: '/auth/verify-email-change',
    name: 'VerifyEmailChange',
    component: () => import('@/views/auth/VerifyEmailChange.vue'),
    meta: {
      title: 'Verificar Cambio de Email - Olvidadme'
    }
  },
  {
    path: '/auth/confirm-deletion',
    name: 'ConfirmDeletion',
    component: () => import('@/views/auth/ConfirmDeletion.vue'),
    meta: {
      title: 'Confirmar Eliminacion de Cuenta - Olvidadme',
      robots: 'noindex, nofollow'
    }
  },
  {
    path: '/auth/cancel-deletion',
    name: 'CancelDeletion',
    component: () => import('@/views/auth/CancelDeletion.vue'),
    meta: {
      title: 'Cancelar Eliminacion de Cuenta - Olvidadme',
      robots: 'noindex, nofollow'
    }
  },

  // ==========================================
  // PAYMENT ROUTES (Public access, post-Stripe redirect)
  // ==========================================
  
  {
    path: '/payment/success',
    name: 'PaymentSuccess',
    component: () => import('@/views/payment/Success.vue'),
    meta: {
      title: 'Registro Completado - Olvidadme',
      description: 'Tu registro se ha completado exitosamente. Bienvenido a Olvidadme.',
      robots: 'noindex, nofollow' // Payment pages should not be indexed
    }
  },
  {
    path: '/payment/cancelled',
    name: 'PaymentCancelled',
    component: () => import('@/views/payment/Cancelled.vue'),
    meta: {
      title: 'Pago Cancelado - Olvidadme',
      description: 'Has cancelado el proceso de pago. Puedes intentar de nuevo en cualquier momento.',
      robots: 'noindex, nofollow'
    }
  },
  {
    path: '/payment/failed',
    name: 'PaymentFailed',
    component: () => import('@/views/payment/Failed.vue'),
    meta: {
      title: 'Error en el Pago - Olvidadme',
      description: 'Ha ocurrido un error procesando tu pago. Por favor, inténtalo de nuevo.',
      robots: 'noindex, nofollow'
    }
  },
  {
    path: '/payment/history',
    name: 'PaymentHistory',
    component: () => import('@/views/payment/PaymentHistory.vue'),
    meta: {
      requiresAuth: true,
      title: 'Historial de Pagos - Olvidadme',
      description: 'Consulta el historial completo de tus pagos y transacciones.',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'Historial de Pagos', path: '/payment/history' }
      ]
    }
  },

  // ==========================================
  // LEGAL PAGES ROUTES (Public access, GDPR compliance)
  // ==========================================
  
  {
    path: '/politica-privacidad',
    name: 'PrivacyPolicy',
    component: () => import('@/views/legal/PrivacyPolicy.vue'),
    meta: {
      title: 'Política de Privacidad - Olvidadme',
      description: 'Política de privacidad y protección de datos personales según GDPR'
    }
  },
  {
    path: '/terminos-condiciones',
    name: 'TermsConditions',
    component: () => import('@/views/legal/TermsConditions.vue'),
    meta: {
      title: 'Términos y Condiciones - Olvidadme',
      description: 'Términos y condiciones de uso de nuestros servicios legales'
    }
  },
  {
    path: '/aviso-legal',
    name: 'LegalNotice',
    component: () => import('@/views/legal/LegalNotice.vue'),
    meta: {
      title: 'Aviso Legal - Olvidadme',
      description: 'Información legal sobre la empresa y servicios profesionales'
    }
  },
  {
    path: '/politica-cookies',
    name: 'CookiePolicy',
    component: () => import('@/views/legal/CookiePolicy.vue'),
    meta: {
      title: 'Política de Cookies - Olvidadme',
      description: 'Información sobre el uso de cookies y tecnologías similares'
    }
  },
  {
    path: '/centro-ayuda',
    name: 'HelpCenter',
    component: () => import('@/views/support/HelpCenter.vue'),
    meta: {
      title: 'Centro de Ayuda - Olvidadme',
      description: 'Preguntas frecuentes y soporte para nuestros servicios'
    }
  },
  {
    path: '/quienes-somos',
    name: 'AboutUs',
    component: () => import('@/views/company/AboutUs.vue'),
    meta: {
      title: 'Quiénes Somos - Olvidadme | El equipo detrás de tu libertad digital',
      description: 'Conoce a Yolanda Moy y Jorge Aznar, el equipo fundador de Olvidadme. Más de 20 años de experiencia en derecho y tecnología digital.',
      keywords: 'equipo olvidadme, derecho al olvido, legado digital, privacidad digital, GDPR',
      ogType: 'website',
      robots: 'index, follow'
    }
  },
  {
    // Página B2B discreta: se comparte por enlace con profesionales con contacto previo.
    // NO se enlaza desde header, footer, navigation.js ni sitemap (decisión del sprint
    // colaboradores-generico).
    path: '/colaboradores',
    name: 'Partners',
    component: () => import('@/views/partners/Partners.vue'),
    meta: {
      title: 'Colaboración con profesionales | Olvidadme',
      description: 'Recomiende a sus clientes un servicio legal especializado en legado digital, testamento digital y derecho al olvido. Sin coste operativo para ustedes.',
      ogType: 'website',
      robots: 'noindex, nofollow'
    }
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: () => import('@/views/pricing/Pricing.vue'),
    meta: {
      title: 'Servicios y Precios - Olvidadme | Protección Digital Profesional',
      description: 'Elige el servicio que mejor se adapte a tus necesidades: Derecho al Olvido (499€), Post-Mortem (699€) o Testamento Digital (599€).',
      keywords: 'precios, servicios, derecho al olvido, post-mortem, testamento digital, GDPR, protección datos'
    }
  },
  {
    path: '/como-funciona',
    name: 'HowItWorks',
    component: () => import('@/views/HowItWorks.vue'),
    meta: {
      title: 'Cómo Funciona - Olvidadme | Proceso de Eliminación de Datos',
      description: 'Conoce el proceso paso a paso para ejercer tu derecho al olvido. 4 etapas simples con plazos reales y transparencia total.',
      keywords: 'proceso, como funciona, derecho al olvido, GDPR, pasos, plazos'
    }
  },
  {
    path: '/support',
    name: 'Support',
    component: () => import('@/views/support/Support.vue'),
    meta: {
      title: 'Centro de Soporte - Olvidadme | Ayuda Especializada',
      description: 'Obtén ayuda especializada según tu plan de protección digital. Soporte diferenciado por tier con tiempos de respuesta garantizados.',
      keywords: 'soporte, ayuda, contacto, tier, plan, respuesta, abogado'
    }
  },
  {
    path: '/support/contact',
    name: 'Contact',
    component: () => import('@/views/support/Contact.vue'),
    meta: {
      title: 'Contacto - Olvidadme',
      description: 'Contacta con nuestro equipo de expertos legales para resolver tus dudas',
      keywords: 'contacto, soporte, ayuda legal, consultas GDPR, atención cliente'
    }
  },
  {
    path: '/legal/rgpd-info',
    name: 'RgpdInfo',
    component: () => import('@/views/legal/RgpdInfo.vue'),
    meta: {
      title: 'Información RGPD - Olvidadme',
      description: 'Todo sobre el Reglamento General de Protección de Datos y tus derechos',
      keywords: 'RGPD, GDPR, derechos datos, protección datos, privacidad, derecho olvido'
    }
  },

  // ==========================================
  // SERVICE CATALOG ROUTES (Public access)
  // ==========================================

  // Redirect /services to /pricing (consolidation per ADR-032)
  {
    path: '/services',
    redirect: '/pricing'
  },
  {
    path: '/services/derecho-olvido',
    name: 'ServiceDetailOlvido',
    component: () => import('@/views/services/ServiceDetail.vue'),
    props: { serviceId: 'derecho-olvido' },
    meta: {
      title: 'Derecho al Olvido - Servicios - Olvidadme'
    }
  },
  {
    path: '/services/derecho-olvido-postmortem',
    name: 'ServiceDetailPostMortem',
    component: () => import('@/views/services/ServiceDetail.vue'),
    props: { serviceId: 'derecho-olvido-postmortem' },
    meta: {
      title: 'Derecho al Olvido Post-Mortem - Servicios - Olvidadme'
    }
  },
  // Redirects for legacy/inconsistent naming
  {
    path: '/services/gestion-post-mortem',
    redirect: '/services/derecho-olvido-postmortem'
  },
  {
    path: '/services/gestion-postmortem',
    redirect: '/services/derecho-olvido-postmortem'
  },
  {
    path: '/services/testamento-digital',
    name: 'ServiceDetailTestamento',
    component: () => import('@/views/services/ServiceDetail.vue'),
    props: { serviceId: 'testamento-digital' },
    meta: {
      title: 'Testamento Digital - Servicios - Olvidadme'
    }
  },
  // Redirección intencionada — servicio "Consultoría GDPR" eliminado del MVP (2026-04-10)
  {
    path: '/services/consultoria-gdpr',
    redirect: '/pricing'
  },

  // ==========================================
  // PROTECTED ROUTES (Authentication required)
  // ==========================================

  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/components/dashboard/tier-aware/TierAwareDashboard.vue'),
    meta: {
      requiresAuth: true,
      title: 'Dashboard - Olvidadme',
      breadcrumb: [
        { name: 'Dashboard', path: '/dashboard' }
      ]
    }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/profile/Profile.vue'),
    meta: {
      requiresAuth: true,
      title: 'Mi perfil y configuración - Olvidadme',
      description: 'Gestiona tu información personal, seguridad, notificaciones y preferencias de privacidad',
      breadcrumb: [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'Mi perfil', path: '/profile' }
      ]
    }
  },
  {
    // Legacy redirect: /settings now consolidated into /profile
    // Maintains backward compatibility for bookmarks and external links
    path: '/settings',
    redirect: '/profile'
  },
  {
    path: '/cases',
    name: 'CaseList',
    component: () => import('@/views/cases/CasesWithLayout.vue'),
    meta: {
      requiresAuth: true,
      // All service tiers can manage cases (1 case per user)
      title: 'Mis Casos - Olvidadme',
      breadcrumb: [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'Mis Casos', path: '/cases' }
      ]
    }
  },
  {
    // Case creation is now handled via drawer in dashboard
    // Redirect to dashboard for legacy URLs and bookmarks
    path: '/cases/create',
    redirect: '/dashboard'
  },
  {
    path: '/cases/:id',
    name: 'CaseDetail',
    component: () => import('@/views/cases/CaseDetailWithLayout.vue'),
    meta: {
      requiresAuth: true,
      // All service tiers can view case details
      title: 'Detalle del Caso - Olvidadme',
      breadcrumb: [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'Mis Casos', path: '/cases' },
        { name: 'Detalle del Caso', path: null } // Dynamic route, no fixed path
      ]
    }
  },
  {
    path: '/cases/:caseId/questionnaire',
    name: 'CaseQuestionnaire',
    component: () => import('@/views/cases/CaseQuestionnaire.vue'),
    meta: {
      requiresAuth: true,
      title: 'Cuestionario Testamento Digital - Olvidadme'
    }
  },
  {
    path: '/documents',
    name: 'Documents',
    component: () => import('@/views/documents/DocumentsWithLayout.vue'),
    meta: {
      requiresAuth: true,
      // All service tiers can access documents
      title: 'Mis Documentos - Olvidadme',
      description: 'Gestiona todos tus documentos legales de forma segura con encriptación completa y cumplimiento GDPR.',
      keywords: 'documentos legales, gestión documentos, encriptación, GDPR, documentos seguros',
      breadcrumb: [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'Documentos', path: '/documents' }
      ]
    }
  },
  {
    path: '/documents/capture',
    name: 'DocumentCapture',
    component: () => import('@/views/documents/CaptureViewWithLayout.vue'),
    meta: {
      requiresAuth: true,
      title: 'Capturar Documento - Olvidadme',
      description: 'Captura documentos con tu cámara de forma segura',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'Documentos', path: '/documents' },
        { name: 'Capturar', path: null }
      ]
    }
  },
  {
    path: '/library',
    name: 'DocumentLibrary',
    component: () => import('@/views/library/DocumentLibraryWithLayout.vue'),
    meta: {
      requiresAuth: true,
      title: 'Biblioteca de Documentos - Olvidadme',
      description: 'Descarga plantillas legales para gestionar tu derecho al olvido y proteger tu huella digital.',
      keywords: 'plantillas legales, GDPR, derecho al olvido, documentos legales, descarga PDF',
      breadcrumb: [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'Biblioteca', path: '/library' }
      ]
    }
  },

  // ==========================================
  // GDPR ROUTES (Authentication required - GDPR compliance)
  // ==========================================

  {
    path: '/gdpr/consent',
    name: 'GdprConsent',
    component: () => import('@/views/gdpr/GdprConsent.vue'),
    meta: {
      requiresAuth: true,
      title: 'Gestión de Consentimientos GDPR - Olvidadme',
      description: 'Controla tus consentimientos y permisos de datos según el GDPR',
      keywords: 'GDPR, consentimientos, privacidad, datos personales, control usuario',
      robots: 'noindex, nofollow', // Private GDPR pages should not be indexed
      breadcrumb: [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'GDPR', path: '/gdpr/consent' },
        { name: 'Consentimientos', path: '/gdpr/consent' }
      ]
    }
  },
  {
    path: '/gdpr/export',
    name: 'GdprExport',
    component: () => import('@/views/gdpr/GdprExport.vue'),
    meta: {
      requiresAuth: true,
      title: 'Exportar Mis Datos GDPR - Olvidadme',
      description: 'Descarga una copia completa de tus datos personales según el GDPR',
      keywords: 'exportar datos, GDPR portabilidad, descargar datos personales',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'GDPR', path: '/gdpr/consent' },
        { name: 'Exportar Datos', path: '/gdpr/export' }
      ]
    }
  },
  {
    path: '/gdpr/delete',
    name: 'GdprDelete',
    component: () => import('@/views/gdpr/GdprDelete.vue'),
    meta: {
      requiresAuth: true,
      title: 'Eliminar Cuenta y Datos - Derecho al Olvido - Olvidadme',
      description: 'Ejercer tu derecho al olvido y eliminar permanentemente tu cuenta según GDPR',
      keywords: 'derecho al olvido, eliminar cuenta, GDPR eliminación, borrar datos',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'GDPR', path: '/gdpr/consent' },
        { name: 'Eliminar Cuenta', path: '/gdpr/delete' }
      ]
    }
  },
  {
    path: '/gdpr/data',
    name: 'GdprData',
    component: () => import('@/views/gdpr/GdprData.vue'),
    meta: {
      requiresAuth: true,
      title: 'Mis Datos GDPR - Vista Completa - Olvidadme',
      description: 'Vista completa de todos tus datos personales almacenados según GDPR',
      keywords: 'mis datos GDPR, acceso datos personales, transparencia datos',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'GDPR', path: '/gdpr/consent' },
        { name: 'Mis Datos', path: '/gdpr/data' }
      ]
    }
  },

  // GDPR Data Suppression (RGPD Art. 17)
  {
    path: '/cuenta/supresion-datos',
    name: 'DataSuppression',
    component: () => import('@/views/account/DataSuppressionWithLayout.vue'),
    meta: {
      requiresAuth: true,
      title: 'Supresion de datos - Olvidadme',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'GDPR', path: '/gdpr/consent' },
        { name: 'Supresion de datos', path: '/cuenta/supresion-datos' }
      ]
    }
  },

  // ==========================================
  // ADMIN ROUTES (Admin access required)
  // ==========================================

  // Admin Routes - Panel de administración completo
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: () => import('@/views/admin/AdminDashboard.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Administración - Olvidadme',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Admin', path: '/admin' }
      ]
    }
  },
  {
    path: '/admin/cases',
    name: 'AdminCases',
    component: () => import('@/views/admin/CaseManagement.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Gestión de Casos - Admin',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Admin', path: '/admin' },
        { name: 'Casos', path: '/admin/cases' }
      ]
    }
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: () => import('@/views/admin/UserList.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Usuarios - Admin',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Admin', path: '/admin' },
        { name: 'Usuarios', path: '/admin/users' }
      ]
    }
  },
  {
    path: '/admin/lawyers',
    name: 'AdminLawyers',
    component: () => import('@/views/admin/LawyerManagement.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Gestión de Abogados - Admin',
      description: 'Panel de administración para gestionar abogados de la plataforma',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Admin', path: '/admin' },
        { name: 'Abogados', path: '/admin/lawyers' }
      ]
    }
  },
  {
    path: '/admin/stats',
    name: 'AdminStats',
    component: () => import('@/views/admin/AdminStats.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Estadísticas - Admin',
      description: 'Panel de estadísticas y métricas de rendimiento de la plataforma',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Admin', path: '/admin' },
        { name: 'Estadísticas', path: '/admin/stats' }
      ]
    }
  },
  {
    path: '/admin/payments',
    name: 'AdminPayments',
    component: () => import('@/views/admin/PaymentManagement.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Gestión de Pagos - Admin',
      description: 'Administrar y supervisar todos los pagos de la plataforma',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Admin', path: '/admin' },
        { name: 'Pagos', path: '/admin/payments' }
      ]
    }
  },
  {
    path: '/admin/audit-logs',
    name: 'AdminAuditLogs',
    component: () => import('@/views/admin/AuditLogs.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Registro de Auditoría - Admin',
      description: 'Historial completo de acciones administrativas y eventos de seguridad',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Admin', path: '/admin' },
        { name: 'Auditoría', path: '/admin/audit-logs' }
      ]
    }
  },
  {
    path: '/admin/archivo-legal',
    name: 'AdminArchivoLegal',
    component: () => import('@/views/admin/ArchivoLegal.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Archivo Legal - Admin',
      description: 'Gestionar manifiestos de retencion RGPD y datos suprimidos',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Admin', path: '/admin' },
        { name: 'Archivo Legal', path: '/admin/archivo-legal' }
      ]
    }
  },
  {
    path: '/admin/library',
    name: 'AdminLibrary',
    component: () => import('@/views/admin/LibraryManagement.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Biblioteca de Documentos - Admin',
      description: 'Gestionar documentos legales de la biblioteca',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Admin', path: '/admin' },
        { name: 'Biblioteca', path: '/admin/library' }
      ]
    }
  },

  // ==========================================
  // BLOG ADMIN ROUTES (Admin access required)
  // ==========================================

  {
    path: '/admin/blog',
    name: 'AdminBlog',
    component: () => import('@/views/admin/blog/BlogManager.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Gestión de Blog - Admin',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Admin', path: '/admin' },
        { name: 'Blog', path: '/admin/blog' }
      ]
    }
  },
  {
    path: '/admin/blog/posts/new',
    name: 'AdminBlogPostCreate',
    component: () => import('@/views/admin/blog/PostEditor.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Nuevo Artículo - Admin',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Admin', path: '/admin' },
        { name: 'Blog', path: '/admin/blog' },
        { name: 'Nuevo Artículo', path: '/admin/blog/posts/new' }
      ]
    }
  },
  {
    path: '/admin/blog/posts/:id/edit',
    name: 'AdminBlogPostEdit',
    component: () => import('@/views/admin/blog/PostEditor.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Editar Artículo - Admin',
      robots: 'noindex, nofollow',
      breadcrumb: [
        { name: 'Admin', path: '/admin' },
        { name: 'Blog', path: '/admin/blog' },
        { name: 'Editar Artículo', path: null } // Dynamic route, no fixed path
      ]
    }
  },
  // CategoryManager route removed - categories are managed inline in BlogManager
  // {
  //   path: '/admin/blog/categories',
  //   name: 'AdminBlogCategories',
  //   component: () => import('@/views/admin/blog/CategoryManager.vue'),
  //   meta: {
  //     requiresAuth: true,
  //     requiresAdmin: true,
  //     title: 'Categorías del Blog - Admin'
  //   }
  // },

  // ==========================================
  // PUBLIC BLOG ROUTES (SEO Optimized)
  // ==========================================
  
  {
    path: '/blog',
    name: 'BlogIndex',
    component: () => import('@/views/blog/BlogIndex.vue'),
    meta: {
      title: 'Blog - Olvidadme | Derecho al Olvido y Privacidad Digital',
      description: 'Guías, noticias y recursos sobre el derecho al olvido, privacidad digital y protección de datos personales. Contenido especializado por expertos legales.',
      keywords: 'blog, derecho al olvido, privacidad digital, GDPR, protección de datos, noticias tecnología, legal tech',
      ogType: 'website',
      robots: 'index, follow, max-image-preview:large'
    }
  },
  {
    path: '/blog/:slug',
    name: 'BlogPost',
    component: () => import('@/views/blog/BlogPost.vue'),
    meta: {
      title: 'Artículo - Blog - Olvidadme',
      description: 'Lee nuestros artículos especializados sobre derecho al olvido y privacidad digital',
      keywords: 'artículo, derecho al olvido, blog legal',
      ogType: 'article',
      robots: 'index, follow, max-image-preview:large, max-snippet:-1',
      // Dynamic meta will be set by the component based on post data
      isDynamic: true
    }
  },
  {
    path: '/blog/categoria/:category',
    name: 'BlogCategory', 
    component: () => import('@/views/blog/BlogCategory.vue'),
    meta: {
      title: 'Categoría - Blog - Olvidadme',
      description: 'Explora artículos organizados por categorías sobre derecho al olvido y privacidad digital',
      keywords: 'categoría, blog, derecho al olvido, temas legales',
      ogType: 'website',
      robots: 'index, follow, max-image-preview:large',
      // Dynamic meta will be set by the component based on category data
      isDynamic: true
    }
  },

  // ==========================================
  // ERROR ROUTES
  // ==========================================

  {
    path: '/403',
    name: 'Forbidden',
    component: () => import('@/views/errors/Forbidden.vue'),
    meta: {
      title: 'Acceso Denegado - Olvidadme',
      robots: 'noindex, nofollow'
    }
  },
  {
    path: '/500',
    name: 'ServerError',
    component: () => import('@/views/errors/ServerError.vue'),
    meta: {
      title: 'Error del Servidor - Olvidadme',
      robots: 'noindex, nofollow'
    }
  },
  {
    path: '/maintenance',
    name: 'Maintenance',
    component: () => import('@/views/errors/Maintenance.vue'),
    meta: {
      title: 'Mantenimiento Programado - Olvidadme',
      robots: 'noindex, nofollow'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/errors/NotFound.vue'),
    meta: {
      title: 'Página No Encontrada - Olvidadme',
      robots: 'noindex, nofollow'
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Scroll to top on route change
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

// ==========================================
// NAVIGATION GUARDS
// ==========================================

/**
 * Global navigation guard - OPTIMIZED (Task 2.3)
 * Implements authentication patterns from Basket Base with enhancements:
 *
 * ✅ Enhanced: Caching to prevent redundant initializeAuth calls
 * ✅ Enhanced: Loading state management for better UX
 * ✅ Enhanced: Improved error handling for auth failures
 * ✅ Enhanced: Promise reuse for concurrent navigation
 */
router.beforeEach(async (to, from, next) => {
  try {
    const authStore = useAuthStore()

    // ✅ OPTIMIZED: Only check auth for routes that actually need it
    // This prevents unnecessary API calls on public pages like /, /pricing, /blog, /auth/*, etc.
    // Guest routes (login, register) no longer check session - simpler UX, no delays
    const needsAuthCheck = to.meta.requiresAuth ||
                           to.meta.requiresAdmin ||
                           to.meta.requiresTier

    if (needsAuthCheck) {
      // Initialize auth state only when needed
      if (!authStore.hasInitialized && !authStore.isInitializing) {
        try {
          logger.debug('Router guard triggering auth initialization', {
            route: to.path,
            reason: to.meta.requiresAuth ? 'requiresAuth' :
                    to.meta.requiresAdmin ? 'requiresAdmin' :
                    to.meta.requiresTier ? 'requiresTier' : 'other'
          })

          await authStore.initializeAuth()
        } catch (error) {
          // Auth initialization failed - user is not authenticated
          logger.debug('Auth initialization failed in router guard', {
            error: error.message,
            route: to.path
          })
        }
      } else if (authStore.isInitializing) {
        // Wait for ongoing initialization to complete
        logger.debug('Auth initialization already in progress, waiting...', {
          route: to.path
        })

        try {
          await authStore.initializeAuth() // This will return the cached promise
        } catch (error) {
          logger.debug('Waiting for auth initialization failed', {
            error: error.message,
            route: to.path
          })
        }
      } else {
        // Already initialized, skip
        logger.debug('Auth already initialized, skipping initialization', {
          route: to.path,
          userAuthenticated: authStore.isAuthenticated
        })
      }
    } else {
      // Public route that doesn't need auth check - skip API call
      logger.debug('Public route, skipping auth check', {
        route: to.path
      })
    }

    // Special handling for /dashboard route: redirect admins to admin dashboard
    if (to.path === '/dashboard' && authStore.isAuthenticated && authStore.isAdmin) {
      logger.info('Admin accessing user dashboard, redirecting to admin dashboard', {
        userId: authStore.user?.id,
        role: authStore.user?.role,
        redirectFrom: '/dashboard',
        redirectTo: '/admin'
      })

      return next('/admin')
    }

    // Check if route requires authentication
    if (to.meta.requiresAuth) {
      // ✅ FIX (ADR-XXX): Ensure initialization is complete before checking auth
      // This prevents race conditions where isAuthenticated is false during initialization
      if (authStore.isInitializing) {
        logger.debug('Waiting for auth initialization to complete before auth check', {
          route: to.path
        })
        await authStore.initializeAuth()
      }

      if (!authStore.isAuthenticated) {
        logger.info('Unauthenticated access attempt', {
          route: to.path,
          redirectTo: '/auth/login'
        })

        return next({
          path: '/auth/login',
          query: { redirect: to.fullPath }
        })
      }
      
      // Check admin access
      if (to.meta.requiresAdmin && !authStore.isAdmin) {
        logger.warn('Non-admin access attempt to admin route', {
          route: to.path,
          userId: authStore.user?.id,
          role: authStore.user?.role
        })

        return next('/403')
      }

      // Note: In the new service model (Sprint 03), all service tiers can create cases
      // The restriction is now 1 case per user, enforced by backend middleware
      // No tier-based route blocking is needed for case access
    }
    
    // Route is allowed
    next()
    
  } catch (error) {
    logger.error('Navigation guard error', {
      error: error.message,
      route: to.path,
      isInitializing: useAuthStore().isInitializing,
      hasInitialized: useAuthStore().hasInitialized
    })

    // ✅ ENHANCED: Better error handling for initialization failures (Task 2.3)
    if (error.message.includes('auth') || error.message.includes('session')) {
      // Auth-related errors: clear auth state and redirect to login if needed
      if (to.meta.requiresAuth) {
        logger.warn('Auth error on protected route, redirecting to login', {
          error: error.message,
          route: to.path
        })
        return next('/auth/login')
      }
    }

    // For other errors, allow navigation but don't break the app
    next()
  }
})

/**
 * After each navigation
 * Log successful navigation for analytics
 */
router.afterEach((to, from) => {
  logger.debug('Navigation completed', {
    from: from.path,
    to: to.path,
    authenticated: useAuthStore().isAuthenticated
  })
})

/**
 * Navigation error handler
 */
router.onError((error) => {
  logger.error('Router navigation error', {
    error: error.message,
    stack: error.stack
  })
})

export default router